import React, { useEffect, useState } from "react";
import { Smartphone, Share, Plus, X, ExternalLink } from "lucide-react";

const DISMISS_KEY = "fellowlog-install-dismissed";
const DISMISS_HOURS = 24;

function isStandaloneDisplay() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  // iOS Safari exposes navigator.standalone
  if (typeof navigator !== "undefined" && navigator.standalone) return true;
  return false;
}

function detectEnv() {
  if (typeof navigator === "undefined") {
    return { ios: false, android: false, safari: false, inApp: false };
  }
  const ua = navigator.userAgent || "";
  const ios = /iPad|iPhone|iPod/i.test(ua) && !window.MSStream;
  const android = /Android/i.test(ua);
  // On iOS, "safari" means genuine Safari (not CriOS/FxiOS/EdgiOS/GSA/FBAN...)
  const safariOnIos = ios && /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|GSA|FBAN|FBAV|Instagram|Line/i.test(ua);
  const inApp = ios && !safariOnIos;
  return { ios, android, safari: safariOnIos, inApp };
}

function wasRecentlyDismissed() {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || "0");
    return ts && Date.now() - ts < DISMISS_HOURS * 3600 * 1000;
  } catch {
    return false;
  }
}

/**
 * Install helper
 *   - iOS Safari (not standalone): shows a bottom-sheet with the exact
 *     "Share → Add to Home Screen" instructions and visual callout.
 *   - iOS in another in-app browser (Chrome, Google app, FB, IG, …):
 *     tells the user to open the page in Safari (gives a 1-tap button).
 *   - Android Chrome: catches `beforeinstallprompt` and offers a 1-tap install.
 *   - Already installed/standalone: renders nothing.
 */
export default function InstallPrompt() {
  const [env] = useState(detectEnv);
  const [standalone, setStandalone] = useState(isStandaloneDisplay);
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    if (standalone) return;
    if (wasRecentlyDismissed()) return;
    const t = setTimeout(() => setVisible(true), 2500);
    return () => clearTimeout(t);
  }, [standalone]);

  // Catch Android/Chrome install prompt
  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setStandalone(true);
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  const androidInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setStandalone(true);
      setVisible(false);
    }
    setDeferredPrompt(null);
  };

  const openInSafari = () => {
    // iOS: `x-safari-https://…` deep-links straight into Safari for most
    // in-app browsers (Chrome iOS, Google app). Fallback to a plain URL.
    const url = window.location.href;
    const safariUrl = url.startsWith("https://")
      ? `x-safari-${url}`
      : url;
    try {
      window.location.href = safariUrl;
    } catch {
      window.location.href = url;
    }
  };

  if (standalone || !visible) return null;

  // Decide which card to show
  let body = null;
  let title = "Install FellowLog";

  if (env.inApp) {
    title = "Open in Safari to install";
    body = (
      <>
        <p className="text-sm leading-relaxed">
          You're viewing this inside another app's browser. To add FellowLog
          to your home screen, open it in <strong>Safari</strong> first.
        </p>
        <button
          onClick={openInSafari}
          className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#7CB342] text-white px-4 py-2 text-sm font-medium w-full justify-center"
          data-testid="install-open-in-safari"
        >
          <ExternalLink className="w-4 h-4" /> Open in Safari
        </button>
      </>
    );
  } else if (env.safari) {
    title = "Add FellowLog to your Home Screen";
    body = (
      <>
        <ol className="text-sm leading-relaxed space-y-2">
          <li className="flex items-start gap-2">
            <span className="font-semibold">1.</span>
            <span>
              Tap the <strong>Share</strong> icon
              <Share className="inline-block w-4 h-4 mx-1 align-text-bottom" />
              at the bottom of Safari.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-semibold">2.</span>
            <span>
              Scroll down and tap
              <strong> Add to Home Screen</strong>
              <Plus className="inline-block w-4 h-4 mx-1 align-text-bottom" />.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-semibold">3.</span>
            <span>Tap <strong>Add</strong> — it'll live on your home screen like a native app.</span>
          </li>
        </ol>
      </>
    );
  } else if (env.android && deferredPrompt) {
    title = "Install FellowLog";
    body = (
      <>
        <p className="text-sm leading-relaxed">
          Add FellowLog to your home screen for a faster, full-screen experience that works offline.
        </p>
        <button
          onClick={androidInstall}
          className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#7CB342] text-white px-4 py-2 text-sm font-medium w-full justify-center"
          data-testid="install-android-install"
        >
          <Plus className="w-4 h-4" /> Install app
        </button>
      </>
    );
  } else if (env.android) {
    // Chrome Android but event not fired yet — show manual instructions.
    title = "Install FellowLog";
    body = (
      <>
        <p className="text-sm leading-relaxed">
          Tap the <strong>⋮</strong> menu in Chrome and choose
          <strong> Add to Home screen </strong> or <strong>Install app</strong>.
        </p>
      </>
    );
  } else {
    // Desktop or unsupported — don't bother the user
    return null;
  }

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-0 inset-x-0 z-50 flex justify-center px-3 pb-4 pointer-events-none"
    >
      <div className="pointer-events-auto w-full max-w-md rounded-2xl bg-white shadow-neu border border-neu-muted/20 p-4 relative">
        <button
          onClick={dismiss}
          data-testid="install-dismiss"
          aria-label="Dismiss"
          className="absolute top-3 right-3 p-1 rounded-full hover:bg-neu-bg"
        >
          <X className="w-4 h-4 text-neu-muted" />
        </button>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[#7CB342] flex items-center justify-center text-white font-bold">
            <Smartphone className="w-5 h-5" />
          </div>
          <div className="font-semibold text-neu-text">{title}</div>
        </div>
        {body}
      </div>
    </div>
  );
}
